import VueRouter from 'vue-router';
import VueApollo from 'vue-apollo';
import { ensureEmailVerificationOrRedirect } from '@/apps/verifyEmail/helpers/emailVerificationGate';
import { FETCH_ONBOARDING_STATE } from '@/graphql/account/provider/queries';
import { readDomainCookie, writeDomainCookie } from '../domain_cookie';
import { Account } from '@/models/account';

/**
 * Routes that are still in legacy Sittercity
 */
export const externalRoutes = [
  '/sitter/verify_identity',
  '/sitter/background_check_id_failed',
  '/sitter/background_check_order',
  '/sitter/complete',
] as const;

/**
 * Child care types that use the profile steps route
 */
const CHILD_CARE_TYPES = ['babysitter', 'nanny'] as const;

/**
 * Provider route prefixes for route checking
 */
const PROVIDER_ROUTE_PREFIXES = ['/account/providers/', '/provider/', '/sitter/', '/availability'] as const;

async function sitterOnboardingState(apolloProvider: VueApollo) {
  try {
    const { data } = await apolloProvider.defaultClient.query({
      query: FETCH_ONBOARDING_STATE,
    });
    return data.onboardingState.SitterOnboardingState;
  } catch (error) {
    console.error('Error fetching onboarding state:', error);
    throw new Error('Failed to fetch onboarding state');
  }
}

export const stateToRouteMap: Record<string, string> = {
  ADDRESS: '/sitter/signup/address',
  PROFILE: '/sitter/signup/care-type',
  IDV: '/sitter/verify_identity',
  BGC: '/sitter/background_check_id_failed',
  BGC_SUBMIT: '/sitter/background_check_order',
  PHOTO: '/sitter/complete',
};

/**
 * Extracts a single value from route query parameters, handling both array and single value cases.
 */
function getQueryParam(query: Record<string, string | (string | null)[] | null> | undefined, key: string): string | null {
  if (!query || !query[key]) return null;
  const value = query[key];
  return Array.isArray(value) ? value[0] : value;
}

/**
 * Gets and syncs the additional profile status from query params, URL params, and cookie.
 * Syncs cookie if URL/query param indicates additional profile but cookie doesn't.
 */
function getAndSyncAdditionalProfile(query?: Record<string, string | (string | null)[] | null>): boolean {
  const queryValue = query ? getQueryParam(query, 'additionalProfile') : null;
  const urlParams = new URLSearchParams(window.location.search);
  const urlValue = urlParams.get('additionalProfile');
  const cookieValue = readDomainCookie('additional_profile') === 'true';

  const isAdditionalProfile = queryValue === 'true' || urlValue === 'true' || cookieValue;

  // Sync cookie if URL/query param exists but cookie doesn't
  if ((queryValue === 'true' || urlValue === 'true') && !cookieValue) {
    writeDomainCookie('additional_profile', 'true');
  }

  return isAdditionalProfile;
}

/**
 * Gets the current path from router, destinationPath, or window.location.
 */
function getCurrentPath(router?: VueRouter, destinationPath?: string): string {
  return destinationPath || router?.currentRoute?.path || window.location.pathname;
}

/**
 * Checks if a path is a provider route. Need for sitter nav.
 */
function isProviderRoute(path: string): boolean {
  return PROVIDER_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));
}

function isOnOnboardingPage(path: string): boolean {
  if (Object.values(stateToRouteMap).includes(path)) {
    return true;
  }
  return path.startsWith('/sitter/signup/') && path !== '/sitter/signup/account';
}

/** Paths where Variant A should run the onboarding email-verification checkpoint. */
function isSitterOnboardingEmailVerificationPath(path: string): boolean {
  return isOnOnboardingPage(path);
}

function isChildCareType(careType: string | null): boolean {
  return careType !== null && (CHILD_CARE_TYPES as readonly string[]).includes(careType);
}

export const careTypeToPathMap: Record<string, string> = {
  babysitter: '/sitter/signup/profile/steps',
  nanny: '/sitter/signup/profile/steps',
  housekeeping: '/sitter/signup/profile/housekeeping',
  petCare: '/sitter/signup/profile/pet-care',
  companionCare: '/sitter/signup/profile/companion-care',
};

export function isExternalRoute(route: string): boolean {
  return (externalRoutes as readonly string[]).includes(route);
}

function routeForOnboardingState(state: string) {
  return stateToRouteMap[state];
}

/**
 * Helper to navigate to a route.
 * Uses router.push() for SPA routes, window.location.assign() for external routes.
 */
async function navigateToRoute(route: string, router?: VueRouter): Promise<void> {
  const shouldCheckpointEmail =
    isExternalRoute(route) && isSitterOnboardingEmailVerificationPath(route) && !getAndSyncAdditionalProfile();

  if (shouldCheckpointEmail) {
    const outcome = await ensureEmailVerificationOrRedirect('onboarding', route);
    if (outcome !== 'proceed') {
      return;
    }
  }

  if (isExternalRoute(route)) {
    window.location.assign(route);
  } else if (router) {
    router.push(route).catch(() => {
      // Ignore navigation errors (e.g., navigating to the same route)
    });
  } else {
    window.location.assign(route);
  }
}

/**
 * Returns the target route if user should be redirected, null otherwise.
 * Used by guards to determine if a redirect is needed.
 */
async function redirectIfNotOnboarded(
  apolloProvider: VueApollo,
  account: Account | null | undefined,
  router?: VueRouter,
  destinationPath?: string,
  query?: Record<string, string | (string | null)[] | null>
): Promise<string | null> {
  if (!account?.isSitter()) return null;

  const onboardingState = await sitterOnboardingState(apolloProvider);

  // Check for additional profile and sync cookie
  const isAdditionalProfile = getAndSyncAdditionalProfile(query);

  // If the user is adding an additional profile, don't redirect them away from the onboarding flow
  if (isAdditionalProfile) {
    return null;
  }

  if (onboardingState !== 'COMPLETE') {
    const targetRoute = routeForOnboardingState(onboardingState);
    const currentPath = getCurrentPath(router, destinationPath);

    if (currentPath !== targetRoute) {
      return targetRoute;
    }
  }

  return null;
}

/**
 * Returns the target route if user should be redirected, null otherwise.
 * Used by guards to determine if a redirect is needed.
 */
async function redirectIfOnboarded(
  apolloProvider: VueApollo,
  account: Account | null | undefined,
  router?: VueRouter,
  destinationPath?: string,
  query?: Record<string, string | (string | null)[] | null>
): Promise<string | null> {
  if (!account?.isSitter()) return null;

  const currentPath = getCurrentPath(router, destinationPath);

  // Always allow access to profile/complete page, even if onboarding is COMPLETE
  if (currentPath === '/sitter/signup/profile/complete') {
    return null;
  }

  // Redirect from all onboarding pages, but exclude /account (account creation)
  if (!isOnOnboardingPage(currentPath)) {
    return null;
  }

  // Check for additional profile and sync cookie
  const isAdditionalProfile = getAndSyncAdditionalProfile(query);

  // Skip redirect if adding additional profile
  if (isAdditionalProfile) {
    return null;
  }

  // Check onboarding state and redirect if COMPLETE
  const onboardingState = await sitterOnboardingState(apolloProvider);
  if (onboardingState === 'COMPLETE') {
    return '/account/providers/dashboard';
  }

  return null;
}

export {
  sitterOnboardingState,
  redirectIfNotOnboarded,
  redirectIfOnboarded,
  routeForOnboardingState,
  navigateToRoute,
  isChildCareType,
  isProviderRoute,
  isSitterOnboardingEmailVerificationPath,
  getAndSyncAdditionalProfile,
};
